import React from 'react'
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer'
import { LayersControl, TileLayer } from 'react-leaflet'
import { useTranslation } from 'utils/useTranslation'
import { useSelector } from 'react-redux'
import { selectCurrentUser, selectTileProvider } from 'services/slices/auth'

type LayerType = {
  name: string
  key: 'satellite' | 'roadmap' | 'terrain' | 'hybrid'
  checked: boolean
}

type LayerControlProps = { isActive?: boolean }

export const LayerControl: React.FC<LayerControlProps> = ({ isActive }) => {
  const tileProvider = useSelector(selectTileProvider)

  if (tileProvider?.toLowerCase?.() === 'openstreetmap') {
    return <OpenStreetMapLayer />
  }

  if (tileProvider?.toLowerCase?.() === 'heremap') {
    return <HEREMapLayer />
  }

  return <GoogleTileMapLayer isActive={isActive} />
}

const GoogleTileMapLayer: React.FC<LayerControlProps> = ({ isActive }) => {
  const user = useSelector(selectCurrentUser)
  const language = user?.default_language
  const { t } = useTranslation('components.map')

  const layers: Array<LayerType> = [
    { key: 'satellite', name: t('views.satellite'), checked: false },
    { key: 'roadmap', name: t('views.roadmap'), checked: true },
    { key: 'terrain', name: t('views.terrain'), checked: false },
    { key: 'hybrid', name: t('views.hybrid'), checked: false },
  ]

  if (!isActive) {
    return (
      <ReactLeafletGoogleLayer
        type="roadmap"
        apiKey={window.envVariables.REACT_APP_LEAFLET_GOOGLE_API_KEY}
      />
    )
  }

  return (
    <LayersControl position="bottomleft">
      {layers.map((layer) => (
        <LayersControl.BaseLayer checked={layer.checked} name={layer.name} key={layer.name}>
          <ReactLeafletGoogleLayer
            type={layer.key}
            styles={[
              {
                featureType: 'administrative',
                elementType: 'geometry',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
              {
                featureType: 'poi',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
              {
                featureType: 'road',
                elementType: 'labels.icon',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
              {
                featureType: 'transit',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
            ]}
            googleMapsLoaderConf={{
              apiKey: window.envVariables.REACT_APP_LEAFLET_GOOGLE_API_KEY,
              language,
            }}
          />
        </LayersControl.BaseLayer>
      ))}
    </LayersControl>
  )
}

const OpenStreetMapLayer: React.FC = () => {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
  )
}

const HEREMapLayer: React.FC = () => {
  const apiKey = window.envVariables.REACT_APP_HERE_MAP_API_KEY

  return (
    <TileLayer
      attribution="&copy; HERE 2024"
      url={`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?apiKey=${apiKey}`}
      maxZoom={20}
    />
  )
}
